<template>
	<div class="form-wrapper">
		<div v-if="notice.message" :class="`notice notice--${notice.type}`">
			<div class="notice__message">
				<p>{{ notice.message }}</p>
			</div>
		</div>
		<form class="form" @submit.prevent="confirmDetails" novalidate>
			<personal-information
				v-if="formLabels.personalInformation"
				:formLabels="formLabels.personalInformation"
				:formTooltips="formTooltips"
				:formName="formName"
			></personal-information>
			<component v-bind:is="formName" :formLabels="formLabels.benefit" :formTooltips="formTooltips"></component>
			<div class="form__section-group">
				<div class="row">
					<div :class="['col-12', {'col-md-6 col-lg-8 col-xl-9' : price}]">
						<div class="form__section">
							<div class="row">
								<div class="col-12">
									<div
										:class="['form-field form-field--checkbox',{'form-field--error':$v.formData.optIn.$error}]"
									>
										<input v-model="formData.optIn" id="optin" type="checkbox" />
										<label v-html="formLabels.optIn" for="optin"></label>
										<span
											class="form-field__error"
											v-if="$v.formData.optIn.$error"
										>{{ formLabels.optInError }}</span>
									</div>
								</div>
								<div class="col-12">
									<div
										:class="['form-field form-field--checkbox',{'form-field--error':$v.formData.acceptTerms.$error}]"
									>
										<input v-model="formData.acceptTerms" id="terms" type="checkbox" />
										<label v-html="formLabels.acceptTerms" for="terms"></label>
										<span
											class="form-field__error"
											v-if="$v.formData.acceptTerms.$error"
										>Please tick that you have read and understood our terms and conditions</span>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div v-if="price" class="col-12 col-md-6 col-lg-4 col-xl-3">
						<div class="form__section">
                            <dl class="form__price">
								<span v-if="formTooltips.priceToolTip" class="form-field__tooltip" v-tooltip="formTooltips.priceToolTip">?</span>
                                <template v-for="(value,index) of price">
                                    <dt :key="`dt-${index}`">{{ index }}</dt><dd :key="`dd-${index}`">{{ value | currency }}</dd>
                                </template>
                            </dl>
                        </div>
					</div>
				</div>
			</div>
			<div class="row justify-content-end">
				<div class="col-auto">
					<button type="submit" class="button">Submit</button>
				</div>
			</div>
		</form>
		<div v-if="notice.message" :class="`notice notice--${notice.type}`">
			<i v-if="notice.type == 'success'" class="far fa-check-circle"></i>
			<i v-if="notice.type == 'error'" class="far fa-times-circle"></i>
			<i v-if="notice.type == 'warning'" class="far fa-exclamation-circle"></i>
			<div class="notice__message">
				<p>{{ notice.message }}</p>
			</div>
		</div>
	</div>
</template>
<script>
import { mapGetters } from "vuex";
import {
	required,
	requiredIf,
	sameAs,
	minLength,
	maxLength,
	email
} from "vuelidate/lib/validators";
import { EventBus } from "../../_vue-event-bus";
import { renameKeys } from "../../../_helpers";
import Cookies from "js-cookie";

import http from "../../../_http.js";

// Partial forms
import PersonalInformation from "./partial-forms/_PersonalInformation.vue";
import NtuGymAndSportsMembership from "./partial-forms/_NtuGymAndSportsMembership.vue";
import NctEasyRider from "./partial-forms/_NctEasyRider.vue";
import ChildcareVouchers from "./partial-forms/_ChildcareVouchers.vue";
import TravelInsurance from "./partial-forms/_TravelInsurance.vue";
import DentalInsurance from "./partial-forms/_DentalInsurance.vue";
import HealthAssessment from "./partial-forms/_HealthAssessment.vue";
import CriticalIllness from "./partial-forms/_CriticalIllness.vue";
import PrivateMedicalInsurance from "./partial-forms/_PrivateMedicalInsurance.vue";
import RobinHood from "./partial-forms/_RobinHood.vue";
import Tram2Work from "./partial-forms/_Tram2Work.vue";
import MangoTrentBarton from "./partial-forms/_MangoTrentBarton.vue";
import SaverTrentBarton from "./partial-forms/_SaverTrentBarton.vue";
import HealthCash from "./partial-forms/_HealthCash.vue";
import AnnualTrain from "./partial-forms/_AnnualTrain.vue";
import HealthShieldCashPlan from "./partial-forms/_HealthShieldCashPlan.vue";

// Modals
import SummaryModal from "../modals/SummaryModal.vue";

const niNumberValidation = value => {
	if (typeof value === "undefined" || value === null || value === "") {
		return true;
	}
	return /^\s*[a-zA-Z]{2}(?:\s*\d\s*){6}[a-zA-Z]?\s*$/g.test(value);
};

const checkEmailDomain = email => {
	if(!email) return false;

	let emailLower = email.toLowerCase();
	return emailLower.endsWith("@ntu.ac.uk") || emailLower.endsWith("@fwdmotion.co.uk");
};

export default {
	data: () => ({
		notice: {
			type: "error",
			message: null
		}
	}),
	props: {
		formName: {
			type: String,
			required: true
		},
		formLabels: {
			type: Object,
			required: true
		},
		formTooltips: {
			type: Object
        },
        pricing: {
            type: Object
        },
        singlePrice: {
            type: Object
        },
        healthAssessmentPricing: {
            type: Object
        }
	},
	components: {
		PersonalInformation,
		NtuGymAndSportsMembership,
		NctEasyRider,
		ChildcareVouchers,
		TravelInsurance,
		DentalInsurance,
		HealthAssessment,
		CriticalIllness,
		PrivateMedicalInsurance,
		RobinHood,
		Tram2Work,
		AnnualTrain,
		MangoTrentBarton,
		SaverTrentBarton,
		HealthCash,
		HealthShieldCashPlan
	},
	computed: {
		...mapGetters(["formData", "formSummary"]),
		formConditionals: {
			get() {
				return this.$store.state.formConditionals;
			},
			set(value) {
				this.$store.commit("updateFormConditionals", value);
			}
		},
		isNewFamilyMembership() {
			return (
				this.formData.benefit.action ===
					this.formLabels.benefit.fields.benefitAction1 &&
				(this.formData.benefit.type ===
					this.formLabels.benefit.fields.benefitType2 ||
					this.formData.benefit.type ===
						this.formLabels.benefit.fields.benefitType4)
			);
		},
		isLoading: {
			get() {
				return this.$store.isLoading;
			},
			set(value) {
				this.$store.commit("updateIsLoading", value);
			}
		},
		postData() {
			let endpointsMap = {
					NtuGymAndSportsMembership:
						"/umbraco/api/ntuSportMembership/submit",
					NctEasyRider: "/umbraco/api/nctEasyRider/submit",
					ChildcareVouchers: "/umbraco/api/childcareVouchers/submit",
					TravelInsurance: "/umbraco/api/travelInsurance/submit",
					DentalInsurance: "/umbraco/api/dentalInsurance/submit",
					HealthAssessment: "/umbraco/api/healthAssessment/submit",
					CriticalIllness: "/umbraco/api/criticalIllnessInsurance/submit",
					PrivateMedicalInsurance: "/umbraco/api/privateMedicalInsurance/submit",
					RobinHood: "/umbraco/api/robinHoodCard/submit",
					Tram2Work: "/umbraco/api/tram/submit",
					MangoTrentBarton: "/umbraco/api/mangoTravelCard/submit",
					SaverTrentBarton: "/umbraco/api/trentBartonBusSaver/submit",
					HealthCash: "/umbraco/api/healthCashPlan/submit",
					AnnualTrain: "/umbraco/api/train/submit",
					HealthShieldCashPlan: "/umbraco/api/HealthShieldCashPlan/submit"
				},
				keysMap = {};

			let postData = {
				endpoint: endpointsMap[this.formName],
				data: {
					personalInformation: this.formData.personalInformation,
					benefit: JSON.parse(JSON.stringify(this.formData.benefit)),
					optIn: this.formData.optIn,
					acceptTerms: this.formData.acceptTerms
				}
			};

			// Append family members if the data is present
			if (Object.keys(this.formData.familyMembers[0]).length) {
				postData.data.benefit.familyMembers = JSON.parse(
					JSON.stringify(this.formData.familyMembers)
				);
			}

			// Append assessments if present
			if (this.formData.myAssessments.length) {
				postData.data.benefit.myAssessments = JSON.parse(
					JSON.stringify(this.formData.myAssessments)
				);
			}
			if (this.formData.partnerAssessments.length) {
				postData.data.benefit.partnerAssessments = JSON.parse(
					JSON.stringify(this.formData.partnerAssessments)
				);
			}

			return postData;
        },
        price() {
            let price,
                annualPrice;

            // Store the labels
            let labels = this.formLabels.benefit.fields;

            // If single price
            if(this.singlePrice) {
                let triggerLabel = Object.keys(labels).find(key => labels[key] === this.formData.benefit[this.singlePrice.triggerField]);

                if(triggerLabel === this.singlePrice.triggerFieldLabel) {
                    
                    price = {};

                    if(this.singlePrice.price.monthly) price['Monthly cost'] = this.singlePrice.price.monthly;
                    if(this.singlePrice.price.annual) price['Annual cost'] = this.singlePrice.price.annual;
                }      
            // else health assessment pricing        
            } else if(this.healthAssessmentPricing) {
                
                let mySelections = this.formData.myAssessments,
                    partnerSelections = this.formData.partnerAssessments;

                if(mySelections.length || partnerSelections.length) {

                    let pricing = this.healthAssessmentPricing.prices,
                        annualPricing = this.healthAssessmentPricing.annualPrices,
                        monthly = 0,
                        annual = 0;

                    //price = pricing[Object.keys(labels).find(key => labels[key] === 'Lifestyle Assessment')];
                    
                    for(let selection of mySelections) {
                        monthly += pricing[Object.keys(labels).find(key => labels[key] === selection)];
                        annual += annualPricing[Object.keys(labels).find(key => labels[key] === selection)];
                    }
                    for(let selection of partnerSelections) {
                        monthly += pricing[Object.keys(labels).find(key => labels[key] === selection)];
                        annual += annualPricing[Object.keys(labels).find(key => labels[key] === selection)];
                    }

                    price = {};
                    price['Monthly cost'] = monthly || undefined;
                    price['Annual cost'] = annual || undefined;
                }
               
            // else if generic pricing
            } else if(this.pricing) {

                 // Find the label keys by the given options
                let field1Label = Object.keys(labels).find(key => labels[key] === this.formData.benefit[this.pricing.field1]),
                field2Label = Object.keys(labels).find(key => labels[key] === this.formData.benefit[this.pricing.field2]);

                if(this.pricing.field2) { // If the priciing is layered
                    price = this.pricing.prices[field1Label] ? this.pricing.prices[field1Label][field2Label] : null;
                    annualPrice = this.pricing.annualPrices && this.pricing.annualPrices[field1Label] ? this.pricing.annualPrices[field1Label][field2Label] : null
                } else if(this.pricing.field1) { // Pricing has one layer

                    price = this.pricing.prices[field1Label];
                    annualPrice = this.pricing.annualPrices ? this.pricing.annualPrices[field1Label] : null;

                    // If the pricing features family multipliers
                    if(this.pricing.perFamilyMember && field1Label in this.pricing.perFamilyMember) {
                        let accumulatedPrice = 0;
                        
                        this.formData.familyMembers.forEach(() => {
                            accumulatedPrice += this.pricing.perFamilyMember[field1Label];
                        });

                        price += accumulatedPrice;
                    }
                }

                // If we have a price, format it adding annual costs if defined
                if(price) {
                    let newPrice = {
                        'Monthly cost': price
                    }

                    if(annualPrice) {
                        newPrice['Annual cost'] = annualPrice;
                    } else if(this.pricing.setAnnual) {
                        newPrice['Annual cost'] = price * 12;
                    } else if(this.pricing.onlyAnnual) {
						newPrice = {
							'Annual cost': price
						}
					}

                    price = newPrice;
                }
            }

            return price;
        }
	},
	provide() {
		return {
			$v: this.$v
		};
	},
	watch: {
		"formData.acceptTerms": {
			handler(newVal) {
				if (!newVal) {
					this.formData.acceptTerms = undefined;
				}
			}
		},
		"formData.optIn": {
			handler(newVal) {
				if (!newVal) {
					this.formData.optIn = undefined;
				}
			}
		},
		formData: {
			handler(newVal) {
				this.$v.$reset();
			},
			deep: true
        },
        price: {
            handler(newVal) {
				if(!newVal) return;

                if(newVal['Annual cost']) {
                    this.formData.benefit.cost = newVal['Annual cost'];
                } else if(newVal['Monthly cost']) {
					this.formData.benefit.cost = newVal['Monthly cost'];
				}
            },
            deep: true
        }
	},
	validations() {
		let validations = {
			formData: {
				acceptTerms: { required },
				optIn: { required },
				personalInformation: {
					title: { required },
					firstName: { required },
					lastName: { required },
					emailAddress: { required, email, checkEmailDomain },
					streetAddress: { required },
					townCity: { required },
					postcode: { required },
					employeeNumber: {
						required: requiredIf(function() {
							return !this.formData.personalInformation.niNumber;
						}),
						minLength: minLength(7),
						maxLength: maxLength(7)
					},
					niNumber: {
						required: requiredIf(function() {
							return !this
								.formData.personalInformation.employeeNumber;
						}),
						niNumberValidation
					},
					jobTitle: { required },
					department: { required },
                    grade: { required }
				},
				benefit: {
					action: {
						required: requiredIf(
							this.formConditionals.benefitAction
						)
					},
					type: {
						required: requiredIf(this.formConditionals.benefitType)
					},
					cost: {
						required: requiredIf(this.formConditionals.benefitCost)
					},
					coverLevel: {
						required: requiredIf(this.formConditionals.coverLevel)
					},
					partnerCoverLevel: { required },
					dateOfBirth: { required },
					amendmentDetails: {
						required: requiredIf(
							this.formConditionals.amendmentDetails
						)
                    },
                    amendAction: {
                        required: requiredIf(this.formConditionals.amendAction)
                    },
					fromStation: {
						required: requiredIf(this.formConditionals.fromStation)
					},
					toStation: {
						required: requiredIf(this.formConditionals.toStation)
					},
					photocardNum: {
						required: requiredIf(this.formConditionals.photocardNum)
                    },
                    gender: {
                        required: requiredIf(this.formConditionals.gender)
                    }
				},
				familyMembers: {
					$each: {
						firstName: {
							required: requiredIf(
								this.formConditionals.familyFields
							)
						},
						lastName: {
							required: requiredIf(
								this.formConditionals.familyFields
							)
						},
						dob: {
							required: requiredIf(
								this.formConditionals.familyFields
							)
						},
						gender: {
							required: requiredIf(
								this.formConditionals.familyFields
							)
						},
						relationship: {
							required: requiredIf(
								this.formConditionals.familyFields
							)
						}
					}
				},
				myAssessments: {
					required: requiredIf(this.formConditionals.myAssessments),
					minLength: minLength(1)
				},
				partnerAssessments: {
					required: requiredIf(
						this.formConditionals.partnerAssessments
					),
					minLength: minLength(1)
				}
			}
		};

		// Older validations, need to use the new method - will update as and when

		// Gym fields
		if (this.formName === "NtuGymAndSportsMembership") {
			validations.formData.benefit = Object.assign({
				action: { required },
				type: {
					required: requiredIf(function() {
						return (
							this.formData.benefit.action ===
							this.formLabels.benefit.fields.benefitAction1
						);
					})
                },
                amendAction: {
                    required: requiredIf(function() {
                        return (this.formData.benefit.action && this.formData.benefit.action === this.formLabels.benefit.fields.benefitAction2)
                    })
                }
			});

			validations.formData.familyMembers = {
				$each: {
					firstName: {
						required: requiredIf(function() {
							return (
								this.isNewFamilyMembership ||
								this.formData.benefit.addFamily || 
								this.formData.benefit.amendAction
							);
						})
					},
					lastName: {
						required: requiredIf(function() {
							return (
								this.isNewFamilyMembership ||
								this.formData.benefit.addFamily || 
								this.formData.benefit.amendAction
							);
						})
					},
					dob: {
						required: requiredIf(function() {
							return (
								this.isNewFamilyMembership ||
								this.formData.benefit.addFamily || 
								this.formData.benefit.amendAction
							);
						})
					},
					gender: {
						required: requiredIf(function() {
							return (
								this.isNewFamilyMembership ||
								this.formData.benefit.addFamily || 
								this.formData.benefit.amendAction
							);
						})
					},
					relationship: {
						required: requiredIf(function() {
							return (
								this.isNewFamilyMembership ||
								this.formData.benefit.addFamily || 
								this.formData.benefit.amendAction
							);
						})
					}
				}
			};
		}

		// NTC Card fields
		if (this.formName === "NctEasyRider") {
			validations.formData.benefit = {
				action: { required },
				type: {
					required: requiredIf(function() {
						return (
							this.formData.benefit.action ===
							this.formLabels.benefit.fields.benefitAction1
						);
					})
				}
			};
		}

        // Childcare fields
        if (this.formName === "ChildcareVouchers") {
            validations.formData.benefit.action = { required };
        }

		if (this.formName === "HealthShieldCashPlan") {
			validations.formData.personalInformation.streetAddress = { required: false };
			validations.formData.personalInformation.townCity = { required: false };
			validations.formData.personalInformation.postcode = { required: false };
			validations.formData.personalInformation.jobTitle = { required: false };
			validations.formData.personalInformation.department = { required: false };
			validations.formData.personalInformation.grade = { required: false };
		}

		return validations;
	},
	methods: {
		showError(msg) {
			this.notice.type = "error";
			this.notice.message = msg;
		},
		showSuccess(msg) {
			this.notice.type = "success";
			this.notice.message = msg;
		},
		setPersonalData() {
			let pData = JSON.parse(
				JSON.stringify(this.formData.personalInformation)
			);
			pData.employeeNumber = undefined;
			pData.niNumber = undefined;

			Cookies.set("ntu-mc-personal-details", pData);
		},
		getPersonalData() {
			if (Cookies.get("ntu-mc-personal-details")) {
				let cookieData = JSON.parse(
					Cookies.get("ntu-mc-personal-details")
				);
				this.formData.personalInformation = cookieData;
			}
		},
		confirmDetails() {
			this.$v.$touch();
			this.$nextTick(() => {
				if (this.$v.$invalid) {
					let errorMessages = document.querySelectorAll(
						".form-field__error, .form-group__error"
					);

					//Not this fix this
					if (errorMessages && errorMessages.length > 0) {
						this.scrollTo(errorMessages[0].parentElement, -20);

						return;
					}
				}

				this.setPersonalData();

				this.showSummary();
			});
		},
		scrollTo(el, offset = 0) {
			// Find the element if the value passed is a selector
			if (typeof el === "string") {
				el = document.querySelector(el);
			}

			let top = el.getBoundingClientRect().top,
				newTop = top + window.scrollY + offset;

			window.scrollTo(0, newTop);
		},
		showSummary() {
			this.$modal.show(
				SummaryModal,
				{
					formData: this.formData,
                    formLabels: this.formLabels,
                    pricing: this.price
				},
				{
					classes: "modal modal--form-summary",
					height: "auto",
					scrollable: true,
					adaptive: true,
					width: 900
				}
			);

			EventBus.$on("submit-details", () => {
				this.submitDetails();
				EventBus.$off();
			});
		},
		submitDetails() {
			this.isLoading = true;

			http.post(this.postData.endpoint, this.postData.data)
				.then(({ data }) => {
					if (data.responseMessage) {
						this.showSuccess(data.responseMessage);
					} else if (data.redirectUrl) {
						window.location = data.redirectUrl;
					}

					this.isLoading = false;
				})
				.catch(({ response }) => {
					if (response.data && response.data.message) {
						this.showError(response.data.message);
					} else {
						this.showError(
							"Something went wrong, please try again later"
						);
					}
					this.$nextTick(() => {
						this.scrollTo(".notice");
						this.isLoading = false;
					});
				});
		}
	},
	mounted() {
		this.getPersonalData();

		// Action is always required
		this.formConditionals.benefitAction = function() {
			return true;
		};
	}
};
</script>